<template>
  <div class="unit-skeleton" aria-busy="true" aria-live="polite">
    <div class="unit-skeleton__container">
      <section class="unit-skeleton__left">
        <div class="unit-skeleton__gallery" />

        <div class="unit-skeleton__tabs">
          <span class="unit-skeleton__tab" />
          <span class="unit-skeleton__tab" />
          <span class="unit-skeleton__tab" />
          <span class="unit-skeleton__tab" />
        </div>

        <div class="unit-skeleton__bubbles">
          <span class="unit-skeleton__bubble" />
          <span class="unit-skeleton__bubble" />
          <span class="unit-skeleton__bubble" />
          <span class="unit-skeleton__bubble" />
        </div>

        <div class="unit-skeleton__text">
          <span class="unit-skeleton__line unit-skeleton__line--w95" />
          <span class="unit-skeleton__line unit-skeleton__line--w90" />
          <span class="unit-skeleton__line unit-skeleton__line--w80" />
          <span class="unit-skeleton__line unit-skeleton__line--w70" />
        </div>

        <div class="unit-skeleton__card unit-skeleton__card--chart" />
        <div class="unit-skeleton__card unit-skeleton__card--finance" />
      </section>

      <aside class="unit-skeleton__right">
        <div class="unit-skeleton__info">
          <span class="unit-skeleton__line unit-skeleton__line--title" />
          <span class="unit-skeleton__line unit-skeleton__line--meta" />
          <span class="unit-skeleton__separator" />
          <span class="unit-skeleton__row">
            <span class="unit-skeleton__line unit-skeleton__line--w40" />
            <span class="unit-skeleton__line unit-skeleton__line--w20" />
          </span>
          <span class="unit-skeleton__row">
            <span class="unit-skeleton__line unit-skeleton__line--w40" />
            <span class="unit-skeleton__line unit-skeleton__line--w20" />
          </span>
          <span class="unit-skeleton__row">
            <span class="unit-skeleton__line unit-skeleton__line--w40" />
            <span class="unit-skeleton__line unit-skeleton__line--w20" />
          </span>
          <span class="unit-skeleton__separator" />
          <span class="unit-skeleton__cta" />
        </div>
      </aside>
    </div>
  </div>
</template>

<script setup lang="ts"></script>

<style scoped lang="scss">
$shimmer-base: rgba(0, 0, 0, 0.06);
$shimmer-highlight: rgba(0, 0, 0, 0.02);

.unit-skeleton {
  width: 100%;
  min-height: 100vh;
  padding-top: 29px;
  margin-bottom: 50px;
}

.unit-skeleton__container {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 416px;
  gap: 24px;
  width: 100%;
  margin: 0 auto;
}

.unit-skeleton__left {
  display: grid;
  gap: 24px;
  min-width: 0;
}

.unit-skeleton__right {
  min-width: 0;
}

// ---- Shared shimmer plate ----

%plate {
  position: relative;
  background: $shimmer-base;
  border-radius: 12px;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      90deg,
      transparent 0%,
      $shimmer-highlight 50%,
      transparent 100%
    );
    transform: translateX(-100%);
    animation: unit-skeleton-shimmer 1.4s ease-in-out infinite;
  }
}

@keyframes unit-skeleton-shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

// ---- Gallery ----

.unit-skeleton__gallery {
  @extend %plate;
  height: 400px;
  border-radius: 16px;
}

// ---- Tabs ----

.unit-skeleton__tabs {
  display: flex;
  gap: 16px;
  padding: 4px 0;
}

.unit-skeleton__tab {
  @extend %plate;
  width: 96px;
  height: 18px;
  border-radius: 6px;
}

// ---- Bubbles ----

.unit-skeleton__bubbles {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.unit-skeleton__bubble {
  @extend %plate;
  width: 110px;
  height: 32px;
  border-radius: 999px;
}

// ---- Text lines ----

.unit-skeleton__text {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 6px 0;
}

.unit-skeleton__line {
  @extend %plate;
  display: block;
  height: 12px;
  border-radius: 4px;
  width: 100%;

  &--w95 {
    width: 95%;
  }
  &--w90 {
    width: 90%;
  }
  &--w80 {
    width: 80%;
  }
  &--w70 {
    width: 70%;
  }
  &--w40 {
    width: 40%;
  }
  &--w20 {
    width: 20%;
  }
  &--title {
    height: 22px;
    width: 70%;
  }
  &--meta {
    height: 14px;
    width: 55%;
  }
}

// ---- Large content cards ----

.unit-skeleton__card {
  @extend %plate;
  border-radius: 16px;

  &--chart {
    height: 280px;
  }
  &--finance {
    height: 220px;
  }
}

// ---- Right-side info block ----

.unit-skeleton__info {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 24px;
  background: var(--surface);
  border: 1px solid var(--border-subtle);
  border-radius: 16px;
  box-shadow: var(--shadow-card);
}

.unit-skeleton__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.unit-skeleton__separator {
  display: block;
  width: 100%;
  height: 1px;
  background: var(--border-subtle);
  margin: 6px 0;
}

.unit-skeleton__cta {
  @extend %plate;
  height: 48px;
  border-radius: 999px;
  margin-top: 6px;
}

// ---- Responsive ----

@media (max-width: 1199px) {
  .unit-skeleton__container {
    grid-template-columns: minmax(0, 1fr) 360px;
  }
}

@media (max-width: 943px) {
  .unit-skeleton__container {
    grid-template-columns: 1fr;
  }

  .unit-skeleton__right {
    order: -1;
  }

  .unit-skeleton__gallery {
    height: 240px;
    border-radius: 12px;
  }
}
</style>
